import { useEffect, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneCard } from '@factorialco/factorial-one-react/dist/experimental'
import {
  Building,
  Hub,
  Laptop,
  Office,
  People,
  Person,
  Salad,
  Timer,
} from '@factorialco/factorial-one-react/icons/app'
import { GetWorkAreaMapsDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useMapEventBus } from 'modules/space_control/contexts/workSpacesMap/MapEventBusContext'
import { useMouse } from 'modules/space_control/hooks/useMouse'
import { WorkSpaceWithBookingNode } from 'modules/space_control/hooks/useWorkSpacesWithBookings'
import { Canvas } from 'modules/space_control/lib/canvas'
import { EVENT } from 'modules/space_control/lib/workSpacesMap/mapEvents'

import { App } from './App'
import EmptyState from './EmptyState'
import LoadingState from './LoadingState'
import { MAP_MODE, WorkSpacesMapProps } from './types'

const SpaceIcon = (type: string) => {
  switch (type) {
    case 'meeting_room':
      return People
    case 'desk':
      return Laptop
    case 'lunch':
      return Salad
    case 'booth':
      return Office
    case 'parking':
      return Building
    case 'other':
      return Hub
  }
  return Hub
}

const HoveredCard = ({ workSpace }: { workSpace: WorkSpaceWithBookingNode }) => {
  return (
    <OneCard
      metadata={[
        {
          icon: Person,
          title: i18n.tp('settings.locations.location.work_spaces.n_people', {
            count: workSpace.originalCapacity || 0,
          }),
          type: 'text',
        },
        {
          icon: SpaceIcon(workSpace.workSpaceTypeValue),
          label: workSpace.workSpaceTypeName,
          type: 'tag',
        },
        {
          icon: Timer,
          label: i18n.t('settings.locations.location.work_spaces.available'),
          status: 'positive',
          type: 'status',
        },
      ]}
      title={workSpace.workSpaceName}
    />
  )
}

export const WorkSpacesMapInternal = ({
  workAreaId,
  workSpaces = [],
  actions,
  mapMode,
}: WorkSpacesMapProps) => {
  const mouse = useMouse()
  const [hovered, setHovered] = useState<WorkSpaceWithBookingNode | null>(null)

  const { data: mapData, loading: mapLoading } = useQuery(GetWorkAreaMapsDocument, {
    variables: {
      workAreaIds: [Number(workAreaId)],
    },
  })

  const map = mapData?.spaceControl?.workAreaMapsConnection?.nodes[0]
  const { eventBus, isReady } = useMapEventBus()

  useEffect(() => {
    if (!eventBus) return

    const handleSpaceHover = ({ workSpaceId }: { workSpaceId: number | null }) => {
      setHovered(workSpaces.find((ws) => ws.workSpaceId === workSpaceId) || null)
      actions.onPinHover?.({ workSpaceId })
    }

    actions.onPinClick && eventBus.on(EVENT.SPACE_CLICK, actions.onPinClick)
    actions.onMapClick && eventBus.on(EVENT.MAP_CLICK, actions.onMapClick)
    eventBus.on(EVENT.SPACE_HOVER, handleSpaceHover)

    return () => {
      actions.onPinClick && eventBus.off(EVENT.SPACE_CLICK, actions.onPinClick)
      actions.onMapClick && eventBus.off(EVENT.MAP_CLICK, actions.onMapClick)
      eventBus.off(EVENT.SPACE_HOVER, handleSpaceHover)
    }
  }, [eventBus, actions, workSpaces])

  useEffect(() => {
    if (!eventBus || !isReady) return

    eventBus.emit(EVENT.UPDATE_WORKSPACES, {
      workSpaces,
    })
  }, [eventBus, workSpaces, isReady])

  useEffect(() => {
    if (!eventBus || !mapMode) return

    if (mapMode === MAP_MODE.PLACE_PIN) {
      eventBus.emit(EVENT.ACTIVATE_MOVE_CURSOR, {})
    } else {
      eventBus.emit(EVENT.RESET_CURSOR, {})
    }
  }, [mapMode, eventBus])

  if (mapLoading || !eventBus) return <LoadingState />
  if (!map || !map.image) return <EmptyState />

  return (
    <Box flexGrow>
      <Box flexGrow style={{ position: 'relative' }}>
        <Canvas>
          {mouse && hovered && (
            <div
              style={{
                pointerEvents: 'none',
                position: 'absolute',
                top:
                  mouse?.clientY + 200 > window.innerHeight
                    ? mouse?.layerY - 200
                    : mouse?.layerY + 10,
                left:
                  mouse?.clientX + 186 >= window.innerWidth
                    ? mouse?.layerX - 400 + (window.innerWidth - mouse?.clientX)
                    : mouse?.layerX - 186,
              }}
              className="w-full max-w-[372px] shadow-xl rounded-lg"
            >
              <HoveredCard workSpace={hovered} />
            </div>
          )}
          <App mapImage={map.image} />
        </Canvas>
      </Box>
    </Box>
  )
}
